import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { showToast } from 'Toast';
import { Geolocation } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
import { type LocationErrorKind } from './useLocationErrorToast.types';
import '../locales/studentNetworkV2/studentNetworkV2-en.json';

const WEB_PERMISSION_DENIED = 1;

// Longer than showToast's default: these are instructions to act on, not confirmations to glance at.
const TIMEOUT_MS = 4000;

// The remedy differs per kind — app permission vs. a device-wide setting vs. retrying — so a
// single generic message would send people to the wrong place.
export async function classifyLocationError(error: unknown): Promise<LocationErrorKind> {
    if (!Capacitor.isNativePlatform()) {
        const code = (error as GeolocationPositionError | undefined)?.code;
        return code === WEB_PERMISSION_DENIED ? 'permissionDenied' : 'unavailable';
    }

    try {
        // checkPermissions never prompts, unlike requestPermissions, so this is safe to call
        // after a failure. Only coarseLocation matters: we never request the fine alias.
        const status = await Geolocation.checkPermissions();
        return status.coarseLocation === 'granted' ? 'unavailable' : 'permissionDenied';
    } catch {
        // The plugin rejects checkPermissions when system location services are disabled
        return 'servicesDisabled';
    }
}

export function useLocationErrorToast() {
    const { t } = useTranslation('back_royal', { keyPrefix: 'studentNetworkV2.studentNetworkV2.errors' });

    return useCallback(
        (kind: LocationErrorKind) => {
            const copy: Record<LocationErrorKind, { message: string; description: string }> = {
                permissionDenied: {
                    message: t('locationPermissionDeniedTitle'),
                    description: t('locationPermissionDeniedBody'),
                },
                servicesDisabled: {
                    message: t('locationServicesDisabledTitle'),
                    description: t('locationServicesDisabledBody'),
                },
                unavailable: {
                    message: t('locationUnavailableTitle'),
                    description: t('locationUnavailableBody'),
                },
            };

            showToast({ ...copy[kind], type: 'warning', timeout: TIMEOUT_MS });
        },
        [t],
    );
}
